import {
  AppBar,
  Badge,
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useAtomValue } from 'jotai';
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLogout } from '../../Authn/useLogout';
import { NOTIFICATIONS } from '../../store/notifications';
import { CID, PID, SUPER_ADMIN } from '../../store/user';
import {
  AccountCircle,
  BluetoothAudio,
  Build,
  CircleNotifications,
  DevicesOther,
  DirectionsRun,
  Edit,
  GpsFixed,
  LocationOn,
  PinDrop,
  Radar,
  Replay,
  Router,
} from '@mui/icons-material';
import { NotificationPanel } from '../../Notifications/NotificationPanel';
import { SHOW_TOOL_FINDER } from '../../store/map';
import { PRIMARY } from '../../Style/GeoMobyBaseTheme';
import { unsafeEntries } from '../../util/typeUtils';

export const NAVIGATION_BAR_HEIGHT = '48px';

const NavigationTabs = () => {
  const logout = useLogout();
  const navigate = useNavigate();
  const isSuperAdmin = useAtomValue(SUPER_ADMIN);
  const showToolFinder = useAtomValue(SHOW_TOOL_FINDER);
  const cid = useAtomValue(CID);
  const pid = useAtomValue(PID);
  const pathname = useLocation().pathname;
  const notifications = useAtomValue(NOTIFICATIONS);

  const [menuItems, setMenuItems] = useState<
    { label: string; path: string; icon?: ReactNode; disabled?: boolean }[]
  >([]);
  const [activeTabKey, setActiveTabKey] = useState<string | null>(null);
  const [accountAnchorEl, setAccountAnchorEl] = useState<HTMLElement | null>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<HTMLElement | null>(null);
  const [tabAnchorEl, setTabAnchorEl] = useState<HTMLElement | null>(null);

  const closeAllPopovers = useCallback(() => {
    setActiveTabKey(null);
    setMenuItems([]);
    setTabAnchorEl(null);
    setNotificationAnchorEl(null);
    setAccountAnchorEl(null);
  }, [setActiveTabKey, setMenuItems, setTabAnchorEl, setNotificationAnchorEl, setAccountAnchorEl]);

  const closeMenuAndNavigate = useCallback(
    (to: string) => () => {
      closeAllPopovers();
      navigate(to);
    },
    [closeAllPopovers, navigate],
  );

  const mapType: 'replay' | 'live' | undefined = useMemo(() => {
    if (pathname === '/replay-map') return 'replay';
    if (pathname === '/live-map') return 'live';
    return undefined;
  }, [pathname]);

  const tabOptions = useMemo(
    () => ({
      Map: [
        { label: 'Live', path: `/live-map`, icon: <LocationOn /> },
        { label: 'Replay', path: '/replay-map', icon: <Replay /> },
        { label: 'Edit', path: '/editor-map', icon: <Edit /> },
        { label: 'Heatmap', path: '/heat-map', icon: <Radar /> },
      ],
      Events: [{ label: 'Events', path: '/events', icon: <DirectionsRun /> }],
      Assets: [
        { label: 'Beacons', path: '/beacons', icon: <BluetoothAudio /> },
        { label: 'Devices', path: '/devices', icon: <DevicesOther /> },
        { label: 'GPS Trackers', path: '/gps-trackers', icon: <GpsFixed /> },
        { label: 'MQTT Gateways', path: '/mqtt-gateways', icon: <Router /> },
        { label: 'Tools', path: '/tools', icon: <Build />, disabled: !showToolFinder },
      ],
      Underground: [{ label: 'Underground Map', path: '/underground', icon: <PinDrop /> }],
    }),
    [showToolFinder],
  );

  const [currentTab, setCurrentTab] = useState<string | null>(null);

  useEffect(() => {
    let newTab: string | null = null;
    let newPath: string | null = pathname;

    if (pathname === '/events-creator') newPath = '/events';
    else if (
      pathname === '/my-account' ||
      pathname === '' ||
      pathname === '/project-config' ||
      pathname === '/super-admin'
    )
      newPath = null;

    if (newPath) {
      for (const [tab, items] of Object.entries(tabOptions)) {
        if (items.some(item => item.path === newPath)) {
          newTab = tab;
        }
      }
    }

    setCurrentTab(newTab);
  }, [pathname, tabOptions]);

  const handleTabHover = (e: React.MouseEvent<HTMLElement>, tabKey: keyof typeof tabOptions) => {
    closeAllPopovers();
    setActiveTabKey(tabKey);
    setMenuItems(tabOptions[tabKey]);
    setTabAnchorEl(e.currentTarget);
  };

  const handleMenuItemClick = (path: string) => {
    closeAllPopovers();
    navigate(path);
  };

  const handleNotificationOpen = (el: HTMLElement) => {
    closeAllPopovers();
    setNotificationAnchorEl(el);
  };

  const handleAccountOpen = (el: HTMLElement) => {
    closeAllPopovers();
    setAccountAnchorEl(el);
  };

  return (
    <div style={{ maxHeight: NAVIGATION_BAR_HEIGHT, display: 'contents' }}>
      <AppBar position="sticky" color="inherit">
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box pl={2} height={22}>
            <img src="./GeoMoby-full-white.svg" alt="Logo Geomoby" height="100%" />
          </Box>

          <Box>
            <Tabs value={currentTab ?? false} variant="scrollable" scrollButtons="auto">
              {unsafeEntries(tabOptions).map(([tabKey, suboptions]) => (
                <Tab
                  key={tabKey}
                  label={tabKey}
                  value={tabKey}
                  disabled={suboptions.length === 0}
                  sx={{ opacity: suboptions.length === 0 ? 0.75 : undefined }}
                  onMouseEnter={e => handleTabHover(e, tabKey)}
                />
              ))}
            </Tabs>

            {/* Tabs Popover */}
            <Popover
              disablePortal
              disableRestoreFocus
              open={Boolean(activeTabKey)}
              anchorEl={tabAnchorEl}
              onClose={closeAllPopovers}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              sx={{ pointerEvents: 'none' }}
              PaperProps={{
                onMouseLeave: closeAllPopovers,
                sx: { pointerEvents: 'auto' },
              }}
            >
              <Stack p={2}>
                {menuItems.map(item => (
                  <Button
                    key={item.label}
                    disabled={item.disabled}
                    onClick={() => handleMenuItemClick(item.path)}
                    startIcon={item.icon}
                    sx={{
                      justifyContent: 'flex-start',
                      color: item.path === pathname ? PRIMARY : 'inherit',
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
            </Popover>
          </Box>

          {/* Account & Notifications */}
          <Box pr={1}>
            <Stack direction="row" spacing={1}>
              <IconButton
                onMouseEnter={e => handleNotificationOpen(e.currentTarget)}
                onClick={e => setNotificationAnchorEl(prev => (prev ? null : e.currentTarget))}
              >
                <Badge
                  color="error"
                  overlap="circular"
                  sx={{ padding: '4px' }}
                  badgeContent={
                    notifications.filter(({ seen, replayed }) =>
                      mapType === 'replay' ? !seen : !seen && !replayed,
                    ).length
                  }
                >
                  <CircleNotifications />
                </Badge>
              </IconButton>

              <IconButton
                onMouseEnter={e => handleAccountOpen(e.currentTarget)}
                onClick={e => setAccountAnchorEl(prev => (prev ? null : e.currentTarget))}
              >
                <AccountCircle />
              </IconButton>
            </Stack>
          </Box>
        </Stack>
      </AppBar>

      {/* Notifications Popover */}
      <Popover
        disablePortal
        disableRestoreFocus
        open={Boolean(notificationAnchorEl)}
        anchorEl={notificationAnchorEl}
        onClose={closeAllPopovers}
        sx={{ pointerEvents: 'none' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          onMouseLeave: closeAllPopovers,
          sx: { pointerEvents: 'auto' },
        }}
      >
        <NotificationPanel map={mapType} />
      </Popover>

      {/* Account Popover */}
      <Popover
        disablePortal
        disableRestoreFocus
        open={Boolean(accountAnchorEl)}
        anchorEl={accountAnchorEl}
        onClose={closeAllPopovers}
        sx={{ pointerEvents: 'none' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          onMouseLeave: closeAllPopovers,
          sx: { pointerEvents: 'auto' },
        }}
      >
        <Stack p={2} alignItems="center">
          <Typography pb={1}>
            <strong>
              {cid} / {pid}
            </strong>
          </Typography>
          {isSuperAdmin && (
            <Button fullWidth onClick={closeMenuAndNavigate('/super-admin')}>
              Super admin
            </Button>
          )}
          <Button fullWidth onClick={closeMenuAndNavigate('/my-account')}>
            My account
          </Button>
          <Button fullWidth onClick={closeMenuAndNavigate('/project-config')}>
            Project Configuration
          </Button>
          <Button fullWidth onClick={closeMenuAndNavigate('/project-selection')}>
            Change project
          </Button>
          <Button fullWidth onClick={logout}>
            Logout
          </Button>
        </Stack>
      </Popover>
    </div>
  );
};

export default NavigationTabs;
